import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import clsx from 'clsx';

import { Loading } from 'components/Loading';
import {
  archiveEmailThread,
  unarchiveEmailThread,
  getThreadCleanMessages,
  hitThreadCache,
  downloadEmailAttachment,
} from 'api/email';
import parse from 'html-react-parser';
import TooltipButton from 'components/Button/TooltipButton';
import { reduceCommaSeparatedString } from 'utils/strings';
import { sanitizeHtml } from '../Notes/utils/html';

import './styles.scss';

const MessageHeader = ({ userDisplayName, userEmail, leadName, leadEmail, message, to, cc, bcc }) => {
  let displayName;

  for (let fromAddressIndex = 0; fromAddressIndex < message.from.length; fromAddressIndex+=1){
    const fromAddress = message.from[fromAddressIndex];
    if (fromAddress.email === userEmail) {
      displayName = userDisplayName;
      break;
    }
    if (fromAddress.email === leadEmail) {
      displayName = leadName;
      break;
    }
  }

  if (!displayName && message.from[0] && message.from[0].name) {
    displayName = message.from[0].name;
  }

  return (
    <>
      <div className="thread__head">
        <div className="thread__user-name">
          {displayName}
        </div>
        {to && (
          <div className="thread__recipient-name thread__recipient-name--to">
            To: {to}
          </div>
        )}
        {cc && (
          <div className="thread__recipient-name thread__recipient-name--cc">
            Cc: {cc}
          </div>
        )}
        {bcc && (
          <div className="thread__recipient-name thread__recipient-name--bcc">
            Bcc: {bcc}
          </div>
        )}
        <div className="thread__subject">
          {message.subject}
        </div>
      </div>
      <div className="thread__date">
        {dayjs(message.date * 1000).format('MMM DD, YYYY')}
      </div>
    </>
  );
};

const Messages = ({
  handleDisplayToggle,
  threadMessages,
  orgId,
  thread,
  userEmail,
  leadEmail,
  setReplyMessage,
  showArchived,
  setShowArchived,
  refreshThreads,
  setDisplayThreadsLoading,
  readOnlyMode,
}) => {
  const threadId = thread.thread_id || thread.id;
  const [downloadError, setDownloadError] = useState({});

  function onReplyClick(message) {
    if (message.from[0].email === userEmail.email) {
      // if the message is from the user, flip the to/from
      // this is flipped back in the index of LeadsEmailer
      message.from = [...message.to, ...message.from]
        .filter((obj) => obj.email !== userEmail.email);
    }

    // if our email is in the cc, remove it
    message.cc = message.cc.filter((obj) => obj.email !== userEmail.email && obj.email !== message.from[0].email);
    message.bcc = message.bcc.filter((obj) => obj.email !== userEmail.email);

    // if the lead's email is not in the cc, add it
    // we check from here since it becomes the to in the index of LeadsEmailer
    if (
      !message.from.find((obj) => obj.email === leadEmail.email) &&
      !message.cc.find((obj) => obj.email === leadEmail.email)
    ) {
      message.cc.push(leadEmail);
    }

    setReplyMessage(message);
    handleDisplayToggle(false);
    window.scrollTo(0, 0);
  }
  async function handleArchiveClick(message) {
    await archiveEmailThread(orgId, thread.lead_id, message.thread_id);
    setDisplayThreadsLoading(true);
    await refreshThreads({ sortArchived: true });
    setShowArchived(true);
  }
  async function handleUnarchiveClick(message) {
    await unarchiveEmailThread(orgId, thread.lead_id, message.thread_id);
    setDisplayThreadsLoading(true);
    await refreshThreads();
    setShowArchived(false);
  }

  async function handleEmailAttachment(attachment, grantId, messageId, leadId) {
    try {
      setDownloadError(prev => ({ ...prev, [messageId]: null }));
      const attachmentId = attachment.id;
      const response = await downloadEmailAttachment(orgId, leadId, grantId, messageId, attachmentId);
      // take base64 encoding and download file
      const { data } = response;
      const binary = window.atob(data.data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i+=1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: attachment.content_type });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError(prev => ({ ...prev, [messageId]: 'Error downloading attachment, please try again' }));
    }
  }

  return (
    <>
      {
        threadMessages[threadId]?.sort((a,b) => b.date - a.date).map((message, index) => {
            const canReply = index === 0 && message.able_to_reply;
            const { to, cc, bcc } = message || {};
            const toString = to && reduceCommaSeparatedString(to.map(rec => rec.email));
            const ccString = cc && reduceCommaSeparatedString(cc.map(rec => rec.email));
            const bccString = bcc && reduceCommaSeparatedString(bcc.map(rec =>  rec.email));
            const isTempMessage = /^temp-/.test(message.id);
            return (
              <div
                className="thread"
                key={message.id}
              >
                <MessageHeader
                  message={message}
                  userDisplayName={userEmail.name}
                  userEmail={userEmail.email}
                  leadName={leadEmail.name}
                  leadEmail={leadEmail.email}
                  to={toString}
                  cc={ccString}
                  bcc={bccString}
                />
                <div className="thread__content">
                  <div className="thread__text">
                    { parse(sanitizeHtml(message.body, false, [], ['target'])) }
                  </div>
                  { message.attachments && message.attachments.length ? (
                    <div className="thread__attachments">
                      {
                        downloadError &&
                        downloadError[message.id] &&
                        <div className="thread__error">{downloadError[message.id]}</div>
                      }
                      { message.attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="thread__attachment"
                          onClick={
                            () => handleEmailAttachment(attachment, message.grant_id, message.id, thread.lead_id)
                          }
                        >
                          {attachment.filename}
                        </div>
                      ))}
                    </div>
                  ): null }

                  { index === 0 && !showArchived && !readOnlyMode && !isTempMessage ? (
                    <>
                      <TooltipButton
                        type="button"
                        className="button"
                        onClick={() => canReply && onReplyClick(message)}
                        disabled={!canReply}
                        mode={!canReply ? 'tooltip' : 'button'}
                        tooltipProps={{
                            message: 'This thread belongs to another member in your organization and is read-only.',
                            tooltipDelay: 50,
                            floatingProps: {
                              placement: 'top',
                            },
                        }}
                      >
                        Reply
                      </TooltipButton>
                      <button
                        type="button"
                        className="button button--unstyled gold--large"
                        onClick={() => handleArchiveClick(message)}
                      >
                        Archive
                      </button>
                    </>
                  ): null}
                  { index === 0 && showArchived && !readOnlyMode && !isTempMessage ? (
                    <button
                      type="button"
                      className="button button--unstyled gold--large"
                      onClick={() => handleUnarchiveClick(message)}
                    >
                      Unarchive
                    </button>
                  ): null}
                  { isTempMessage && (
                    <div className="thread__temp-message">
                      <span>Sending...</span>
                    </div>
                  )}
                </div>
              </div>
            );
          },
        )
      }
    </>
  );
};

const Threads = ({
  title,
  userEmail,
  activeThreads,
  archivedThreads,
  refreshThreads,
  setReplyMessage,
  handleDisplayToggle,
  orgId,
  readOnlyMode,
  leadId,
  leadEmail,
  userGrantId,
  selectedThread,
  setSelectedThread,
  messagesLoading,
  setMessagesLoading,
}) => {
  const [showArchived, setShowArchived] = useState(archivedThreads.length && !activeThreads.length);
  // #TODO: Make this magic number configurable.
  const showAmount = 5;
  const [pageNumber, setPageNumber] = useState(0);
  const [threadMessages, setThreadMessages] = useState({});
  const [displayThreadsLoading, setDisplayThreadsLoading] = useState(true);
  const [displayThreads, setDisplayThreads] = useState(showArchived ? archivedThreads : activeThreads);

  const [totalCount, setTotalCount] = useState(displayThreads.length || 0);
  const totalPages = Math.ceil(totalCount / showAmount);
  const startIndex = pageNumber * showAmount;
  const _endIndex = pageNumber * showAmount + showAmount;
  const endIndex = _endIndex >= totalCount ? totalCount : _endIndex;

  function getThreadMessages(thread) {
    if (!Object.keys(thread).length) {
      return;
    }

    const threadId = thread.thread_id || thread.id;

    let opEmailAddress;
    hitThreadCache(orgId, leadId, threadId)
      .then((cacheMessages) => {
        if (
          cacheMessages.ok &&
          cacheMessages?.data?.length &&
          !threadMessages[threadId] ||
          (
            threadMessages[threadId]?.length &&
            threadMessages[threadId]?.length <= cacheMessages.data.length
          )
        ) {
          setThreadMessages(prev => ({
            ...prev,
            [threadId]: cacheMessages.data,
          }));
          setMessagesLoading(prev => ({
            ...prev,
            [threadId]: false,
          }));
        }

        if (!cacheMessages?.data?.length || !cacheMessages?.data?.sort) {
          return;
        }

        const sortedCacheMessages = cacheMessages.data.sort((a, b) => a.date - b.date);
        opEmailAddress = sortedCacheMessages[0]?.from[0]?.email;
      })
      .catch((e) => {
        /* eslint-disable-next-line no-console */
        console.error('Error fetching thread messages', e);
      })
      .finally(() => {
        if (archivedThreads.length && archivedThreads.find(t => t.id === threadId)) {
          setMessagesLoading(prev => ({
            ...prev,
            [threadId]: false,
          }));
          return;
        }
        getThreadCleanMessages(orgId, leadId, threadId, userGrantId, opEmailAddress).then((apiMessages) => {
          if (apiMessages.ok && apiMessages.data.length && !apiMessages.data.error) {
            setThreadMessages(prev => ({
              ...prev,
              [threadId]: apiMessages.data,
            }));
          }

          setMessagesLoading(prev => ({
            ...prev,
            [threadId]: false,
          }));
        });
      });

    setSelectedThread(thread);
  }

  const showThread = (threadId) => {
    const activeThread = activeThreads.find(thread => thread.id === threadId);
    if (activeThread) {
      setMessagesLoading(prev => ({
        ...prev,
        [threadId]: true,
      }));
      return;
    }

    const archivedThread = archivedThreads.find(thread => thread.id === threadId);
    if (archivedThread) {
      setMessagesLoading(prev => ({
        ...prev,
        [threadId]: true,
      }));
      setShowArchived(true);
    }
  };

  const handleThreadClick = (thread) => {
    setSelectedThread(thread);
    setMessagesLoading(prev => ({
      ...prev,
      [thread.id]: true,
    }));
  };

  useEffect(() => {
    let threadsToDisplay = showArchived ? archivedThreads : activeThreads;
    // We need to de-duplicate threads based on
    // their subject matching,
    // id not matching,
    // and most recent latest_message_received_date
    const uniqueThreads = {};
    threadsToDisplay.forEach((thread) => {
      const subjectKey = thread.subject.toLowerCase();
      if (
        !uniqueThreads[subjectKey] ||
        (uniqueThreads[subjectKey].latest_message_received_date < thread.latest_message_received_date)
      ) {
        uniqueThreads[subjectKey] = thread;
      }
    });
    threadsToDisplay = Object.values(uniqueThreads);
    // Sort threads by latest_message_received_date
    threadsToDisplay.sort((a, b) => b.latest_message_received_date - a.latest_message_received_date);
    // If the selected thread is not in the display threads, reset it
    if (selectedThread.id && !threadsToDisplay.find(thread => thread.id === selectedThread.id)) {
      setSelectedThread({});
    }
    // Set the display threads
    setDisplayThreads(threadsToDisplay);
  }, [showArchived, activeThreads, archivedThreads]);

  useEffect(() => {
    setTotalCount(displayThreads.length);
    // Check for temp-id messages in displayThreads
    const newThreadMessages = { ...threadMessages };
    const displayThreadIds = displayThreads.map(thread => thread.id);
    Object.keys(newThreadMessages).forEach((threadId) => {
      if (!displayThreadIds.includes(threadId)) {
        delete newThreadMessages[threadId];
        if (selectedThread.id === threadId) {
          setSelectedThread({});
        }
      }
    });
    displayThreads.forEach((thread) => {
      // Clean up temp messages that have real counterparts first
      const realMessages = newThreadMessages[thread.id] || [];
      const realMessageBodies = realMessages
        .filter(m => !/^temp-/.test(m.id))
        .map(m => m.body);

      // Filter out temp message IDs that have real counterparts
      const cleanedMessageIds = thread.message_ids.filter(messageId => {
        if (/^temp-/.test(messageId)) {
          const tempBody = thread.temporaryMessages?.[messageId]?.body;
          const tempDate = thread.temporaryMessages?.[messageId]?.date;

          if (tempBody && newThreadMessages[thread.id]?.some(msg => msg.date > tempDate)) {
            return false; // Remove temp message as it has a real counterpart
          }

          // If we don't have the temp body or can't find a match, keep it for now
          return !!tempBody;
        }
        return true;
      });

      // Update the thread's message_ids to the cleaned version
      thread.message_ids = cleanedMessageIds;

      // Clean up temporaryMessages object to remove stale entries
      const cleanedTemporaryMessages = {};
      if (thread.temporaryMessages) {
        cleanedMessageIds.forEach(messageId => {
          if (/^temp-/.test(messageId) && thread.temporaryMessages[messageId]) {
            cleanedTemporaryMessages[messageId] = thread.temporaryMessages[messageId];
          }
        });
        thread.temporaryMessages = cleanedTemporaryMessages;
      }

      // Remove temp messages from threadMessages that have real counterparts
      if (newThreadMessages[thread.id]) {
        newThreadMessages[thread.id] = newThreadMessages[thread.id].filter(msg => {
          if (msg.id.startsWith('temp-')) {
            return !realMessageBodies.includes(msg.body);
          }
          return true;
        });
      }

      // Add new temporary messages that don't have real counterparts
      thread.message_ids.forEach((messageId) => {
        if (/^temp-/.test(messageId)) {
          if (!newThreadMessages[thread.id]) {
            newThreadMessages[thread.id] = [];
          }
          const pendingSubject = `${thread.subject} *`;
          if (!newThreadMessages[thread.id].find(msg => msg.id === messageId)) {
            // Add a temporary message for pending messages
            const pendingMessage = thread.temporaryMessages?.[messageId];
            newThreadMessages[thread.id].push({
              id: messageId,
              subject: pendingSubject,
              body: pendingMessage?.body || 'Message is pending...',
              date: pendingMessage?.date, // Use current time for temp messages
              from: [{ email: userEmail.email, name: userEmail.name }],
              to: [],
              cc: [],
              bcc: [],
              able_to_reply: false,
            });
          }
        }
      });
    });
    setThreadMessages(newThreadMessages);
  }, [displayThreads]);

  useEffect(() => {
    if (!selectedThread?.id && displayThreads.length) {
      setSelectedThread(displayThreads[0]);
      setMessagesLoading(prev => ({
        ...prev,
        [displayThreads[0].id]: true,
      }));
    }
  }, [threadMessages]);

  useEffect(() => {
    if (window.location.hash) {
      showThread(window.location.hash.slice(1));
      // Don't want to push state to history when removing the hash from the location, otherwise
      // will require back twice to get back to previous page.
      window.history.replaceState(null, '', `${window.location.origin}/${window.location.pathname}`);
    }
  }, []);

  useEffect(() => {
    if (selectedThread.id && messagesLoading[selectedThread.id]) {
      getThreadMessages(selectedThread);
    } else if (displayThreads.length && !selectedThread.id) {
      getThreadMessages(displayThreads[0]);
    }
  }, [messagesLoading]);

  useEffect(() => {
    if (!displayThreadsLoading) {
      // TODO: Fix this useEffect to not run twice
      // when archiving and unarchiving threads, the pager will reset
      // we've already loaded the threads, so don't need to do anything
      return;
    }

    if (!displayThreads.length || !selectedThread.id) {
      setPageNumber(0);
      return;
    }

    const threads = showArchived ? archivedThreads : activeThreads;

    const threadIndex = threads.findIndex(thread => thread.id === selectedThread.id);
    if (threadIndex === -1) {
      setPageNumber(0);
      return;
    }

    const threadPageNumber = Math.floor((threadIndex+1) / showAmount);
    setPageNumber(threadPageNumber);
    setDisplayThreadsLoading(false);
  }, [showArchived, displayThreads]);

  return (
    <div className="email-threads">
      {title && (
        <h2 className="email-threads__title">
          { displayThreads[0].thread_archived ? `Archived ${title}` : title }
        </h2>
      )}
      <>
        { archivedThreads.length > 0 && (!displayThreads.length || !displayThreads[0].thread_archived) ? (
          <button
            type="button"
            className="button button--unstyled gold"
            onClick={() => setShowArchived(true)}
          >
            View Archived Emails
          </button>
        ): null }
        { activeThreads.length > 0 && (!displayThreads.length || displayThreads[0].thread_archived) ? (
          <button
            type="button"
            className="button button--unstyled gold"
            onClick={() => setShowArchived(false)}
          >
            View Active Emails
          </button>
        ) : null }

        <div className="threads-container">
          <div className="thread-subjects">
            {displayThreads
              .slice(startIndex, endIndex)
              .map((thread) => {
                const threadSubject = thread.message_ids.some(
                  messageId => /^temp-/.test(messageId),
                ) ? `${thread.subject} *` : thread.subject;
                return (
                  <div
                    key={thread.id}
                    className={clsx(
                      'thread-subject',
                      !displayThreadsLoading && thread.id === selectedThread.id && 'active',
                      displayThreadsLoading && 'loading',
                    )}
                    onClick={() => !displayThreadsLoading && handleThreadClick(thread)}
                  >
                    {threadSubject}
                  </div>
                );
              })
            }
            <div className="threads-pagination pager">
              <div className="thread-pagination__counter">
                {
                  displayThreadsLoading ?
                  'Loading threads...' :
                  `Viewing ${startIndex + 1}-${endIndex} of ${totalCount} messages`
                }
              </div>
              <div className="threads-pagination__controls">
                {!displayThreadsLoading && totalCount > showAmount && (
                  <>
                    <button
                      type="button"
                      className="button--unstyled pager__button"
                      {... pageNumber === 0 ? {
                        disabled: true,
                      } : {
                        onClick: () => setPageNumber(pageNumber - 1),
                      }}
                    >
                      Prev
                    </button>
                    |
                    <button
                      type="button"
                      className="button--unstyled pager__button"
                      {... (pageNumber + 1)>= totalPages ? {
                        disabled: true,
                      } : {
                        onClick: () => setPageNumber(pageNumber + 1),
                      }}
                    >
                      Next
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="thread-messages">
            {
              selectedThread.id && messagesLoading[selectedThread.id] && <Loading />
            }
            {
              selectedThread.id &&
              !messagesLoading[selectedThread.id] && (
                <Messages
                  threadMessages={threadMessages}
                  thread={selectedThread}
                  orgId={orgId}
                  userEmail={userEmail}
                  leadEmail={leadEmail}
                  setReplyMessage={setReplyMessage}
                  showArchived={showArchived}
                  setShowArchived={setShowArchived}
                  handleDisplayToggle={handleDisplayToggle}
                  refreshThreads={refreshThreads}
                  setDisplayThreadsLoading={setDisplayThreadsLoading}
                  readOnlyMode={readOnlyMode}
                  userGrantId={userGrantId}
                />
              )
            }
          </div>
        </div>
      </>
    </div>
  );
};

Threads.propTypes = {
  activeThreads: PropTypes.array,
  archivedThreads: PropTypes.array,
};

export default Threads;
